'use client';

import * as React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';

import { Search, User, MapPin } from 'lucide-react';
import {
  HeaderLayoutProps,
  HeaderMenuElementDataType,
} from '@/components/sections/builder/elements/header/header/type';
import { IMenuItem } from '@/types/navigation';
import { cn } from '@/lib/utils';
import { HeaderSubMenuType } from '@/components/sections/builder/settings/header-sub-menu-setting';
import HeaderLogo from '@/components/sections/builder/elements/header/header/header-logo';
import { UserNav } from '@/components/sections/builder/elements/header/header/user-nav';
import CartButton from '@/components/sections/builder/elements/header/header/cart-button';
import { ThemeType } from '@/stores/builder';
import { SearchIconButton } from '@/components/sections/builder/elements/header/header/content/search-button';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import useMediaQuery from '@/hooks/useMediaQuery';
import { NavigationMenuMobile } from '@/components/sections/builder/elements/header/header/content/nav-menu-mobile';
import { NavigationMenuDesktop } from '@/components/sections/builder/elements/header/header/content/nav-menu';

export function MegaNavbar({
  data,
  menuItems,
  isMegaMenu,
  preview,
  searchModal,
  token,
  themeId,
  tokenPreview,
  theme,
  modalMultiOrigin,
  selectedLocation,
  locationToDisplay,
  multiOrigin,
  setCollapsedAction,
  isLoadingMenu,
}: HeaderLayoutProps) {
  const isMobile = useIsMobile();
  const isLtLg = useMediaQuery(1024);
  const isLtMd = useMediaQuery(640);
  const isDesktop = !isLtLg;
  const isTablet = isLtLg && !isLtMd;

  const [isNavOpen, setIsNavOpen] = React.useState(false);
  const [logoW, setLogoW] = React.useState('9rem');

  const logoRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const el = logoRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect().width;
    setLogoW(`${Math.ceil(w) + 24}px`);
  }, [data?.logoUrl]);

  const dataMenus = transformMenuToHeaderData(menuItems, isMegaMenu);

  const getMenuJustify = (menuPosition?: string) => {
    if (menuPosition) {
      const justifyMap: Record<string, string> = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
      };
      return justifyMap[menuPosition] || 'justify-start';
    }
    return 'justify-start';
  };

  const getLayoutClasses = () => {
    switch (data.logoPosition) {
      case 'top-left': {
        return {
          container:
            'relative justify-between items-center flex flex-row lg:flex-col md:pt-[var(--topbar-h,3.25rem)]',
          logo:
            'flex items-center space-x-2 flex-shrink-0 pl-11 lg:pl-0 ' +
            'md:absolute md:left-0 ' +
            'md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
          nav: 'hidden lg:flex items-center w-full justify-start md:pt-[var(--logo-nav-gap,0.75rem)]',
          actions:
            'flex md:items-center gap-2 ' +
            'md:absolute md:right-0 ' +
            'md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
          mobileNav: 'flex lg:hidden items-center justify-start absolute top-1/2 -translate-y-1/2',
        };
      }
      case 'top-center': {
        return {
          container:
            'relative flex justify-between items-center lg:flex-col md:pt-[var(--topbar-h,3.25rem)]',
          logo: 'flex items-center space-x-2 flex-shrink-0 z-10 md:absolute md:left-1/2 md:-translate-x-1/2 md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
          nav: 'hidden lg:flex items-center w-full md:pt-[var(--logo-nav-gap,0.75rem)]',
          actions:
            'flex items-center gap-2 w-full justify-end md:absolute md:right-0 md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
          mobileNav:
            'lg:hidden flex items-center justify-start w-full md:absolute md:left-0 md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
          leftSearch:
            'hidden lg:flex items-center gap-2 z-10 md:absolute md:left-0 md:top-[calc(var(--topbar-h,3.25rem)/2)] md:-translate-y-1/2',
        };
      }
      case 'middle-left': {
        const menuJustify = getMenuJustify(data.middleLeftMenuPosition);
        return {
          container: 'flex items-center md:justify-between relative',
          logo: 'z-20 flex items-center space-x-2 flex-shrink-0 md:absolute md:left-12 lg:left-0 md:top-1/2 md:-translate-y-1/2',
          nav: `hidden md:flex items-center lg:w-full ${menuJustify} lg:absolute md:pl-[var(--logo-w)]`,
          actions: 'flex md:items-center gap-2 right-0 justify-end z-10 w-full',
          mobileNav: 'left-0 lg:hidden items-center justify-start',
        };
      }
      case 'middle-center': {
        return {
          container: 'flex items-center md:justify-between relative',
          logo: 'z-20 md:absolute md:left-1/2 md:transform md:-translate-x-1/2 flex items-center space-x-2 flex-shrink-0',
          nav: 'hidden md:flex items-center lg:w-full justify-start',
          actions: 'flex md:items-center gap-2 right-0 w-full justify-end',
          mobileNav: 'left-0 lg:hidden items-center justify-start w-full',
        };
      }
      default: {
        return {
          container: 'grid grid-cols-[auto_1fr_auto] items-center h-16',
          logo: 'flex items-center space-x-2 shrink-0',
          nav: 'hidden md:flex items-center justify-end',
          actions: 'flex items-center gap-2 justify-self-end',
          mobileNav: 'hidden lg:flex items-center justify-end',
        };
      }
    }
  };

  const layoutClasses = getLayoutClasses();

  // Force search to modal if layout is middle-left
  let effectiveSearchBarPosition = 'modal';

  if (data?.logoPosition === 'middle-left') {
    if (isDesktop) {
      effectiveSearchBarPosition = 'modal';
    } else if (isTablet) {
      effectiveSearchBarPosition = 'header';
    } else {
      effectiveSearchBarPosition = data?.searchBarPosition || 'modal';
    }
  } else {
    effectiveSearchBarPosition = data?.searchBarPosition || 'modal';
  }

  const isModalSearch = data.showSearch && effectiveSearchBarPosition === 'modal';
  const isHeaderSearch = data.showSearch && effectiveSearchBarPosition === 'header';

  const showLeftModalButton = data.logoPosition === 'top-center' && isModalSearch && isDesktop;
  const showRightModalButton =
    isModalSearch &&
    (isDesktop || isMobile || isTablet) &&
    (isDesktop ? data.logoPosition !== 'top-center' : true);

  return (
    <>
      {isNavOpen && (
        <div
          className={cn(
            'absolute left-0 top-full z-40 h-screen w-full bg-black/20 backdrop-blur-sm',
            'transition-opacity duration-300 ease-in-out'
          )}
          onClick={() => setIsNavOpen(false)}
        />
      )}
      <nav
        className={cn(
          'h-full w-full px-4 py-2',
          data?.styleWidth === 'box' && 'theme_layout_page_width mx-auto'
        )}
      >
        <div
          className={layoutClasses.container}
          style={
            {
              '--logo-w': isMobile ? 50 : logoW,
              '--topbar-h': (data?.logoWidth ?? 0) < 150 ? '2.7rem' : '3.5rem',
              '--logo-nav-gap': '1rem',
            } as React.CSSProperties
          }
        >
          {/* Mobile Navigation */}
          <NavigationMenuMobile
            layoutClasses={layoutClasses}
            dataMenus={dataMenus}
            data={data}
            themeId={themeId}
            token={token}
            tokenPreview={tokenPreview}
            preview={preview}
            setCollapsedAction={setCollapsedAction}
            createUrlAction={createUrl}
          />

          {/* Search mode Logo Left-Center */}
          {showLeftModalButton && (
            <div className={layoutClasses.leftSearch}>
              <SearchIconButton onClick={searchModal.open} />
            </div>
          )}

          {/* Logo */}
          <div
            ref={logoRef}
            className={cn(layoutClasses.logo, 'relative h-[var(--topbar-h)] overflow-hidden')}
          >
            <HeaderLogo {...data} />
          </div>

          {/* Desktop Navigation with Mega Menu */}
          <NavigationMenuDesktop
            menuItems={menuItems}
            layoutClasses={layoutClasses}
            data={data}
            isMegaMenu={isMegaMenu}
            themeId={themeId}
            tokenPreview={tokenPreview}
            preview={preview}
            setIsNavOpenAction={setIsNavOpen}
            createUrlAction={createUrl}
            isLoadingMenu={isLoadingMenu}
          />

          {/* Right side actions */}
          <div className={cn(layoutClasses.actions, 'lg:gap-x-4')}>
            {showRightModalButton && <SearchIconButton onClick={searchModal.open} />}

            {isHeaderSearch && (
              <Input
                name='keyword'
                type='text'
                data-test-id='header-search-input'
                placeholder='Search...'
                className={cn(
                  'theme_input h-2 w-full py-4 placeholder:text-[--theme-input-color] lg:flex-none'
                )}
                onClick={() => searchModal.open()}
                classNameWrapper={cn(
                  'w-full md:w-auto hidden md:block w-full',
                  data.logoPosition?.includes('center') && 'max-w-[150px]'
                )}
                style={
                  {
                    '--theme-input-color': data?.colorSchemeHeader?.text || '#fff',
                  } as React.CSSProperties
                }
                rightContent={
                  <button
                    type='button'
                    className={cn('', data.logoPosition === 'middle-left' && 'lg:hidden')}
                  >
                    <Search className='h-4 w-4' />
                  </button>
                }
              />
            )}

            {multiOrigin && (
              <div className={cn('hidden items-center justify-center gap-x-2 lg:block')}>
                <div
                  role='button'
                  onClick={() => {
                    modalMultiOrigin?.open();
                  }}
                  className='flex w-full items-center justify-center gap-x-2'
                >
                  <MapPin className='h-5 w-5' />
                  <span className='whitespace-nowrap font-medium underline-offset-4 hover:underline'>
                    {selectedLocation || locationToDisplay
                      ? selectedLocation
                        ? selectedLocation.location_name ?? ''
                        : locationToDisplay?.location_name ?? ''
                      : 'Delivery from'}
                  </span>
                </div>
              </div>
            )}
            {/* <Button variant='ghost' size='icon' className='hidden md:inline-flex'>
              <Heart className='w-5 h-5' />
            </Button> */}
            <div className='hidden lg:block'>
              {token ? (
                <UserNav />
              ) : (
                <Link
                  href={`/account/signin${
                    tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                  }`}
                >
                  <Button variant='ghost' size='icon'>
                    <User className='h-6 w-6' />
                  </Button>
                </Link>
              )}
            </div>
            <CartButton
              host={typeof window !== 'undefined' ? window.location.host : ''}
              preview={!!preview}
              theme={theme as ThemeType}
            />
          </div>
        </div>
      </nav>

      <style jsx global>
        {`
          .menu-item-with-hover-border::after {
            background-color: ${data?.colorSchemeMenuHeader?.textSecondary || '#fff'};
          }
        `}
      </style>
    </>
  );
}
const createUrl = (
  url_path: string,
  opts?: { themeId?: string; token?: string; preview?: boolean }
) => {
  if (!url_path) return '#';

  const isExternal = /^https?:\/\//i.test(url_path);
  const base = isExternal ? url_path : `/${url_path.replace(/^\/+/, '')}`;

  const appendQuery = (url: string, qs: string) => {
    if (!qs) return url;
    const [pathAndQuery, hash = ''] = url.split('#');
    const sep = pathAndQuery.includes('?') ? '&' : '?';
    const withQs = `${pathAndQuery}${sep}${qs}`;
    return hash ? `${withQs}#${hash}` : withQs;
  };

  if (!isExternal && opts && (opts.token || opts.preview)) {
    const q = new URLSearchParams();
    if (opts.themeId) q.set('theme_id', opts.themeId);
    if (opts.token) q.set('token', opts.token);
    if (opts.preview) q.set('isPreview', 'true');

    return appendQuery(base, q.toString());
  }

  return base;
};

const flattenChildrenToSubMenus = (
  children: IMenuItem[] | undefined,
  subMenus: HeaderSubMenuType[] = [],
  groupIndex: number = 0
): HeaderSubMenuType[] => {
  if (!children) return subMenus;
  for (const child of children) {
    subMenus.push({
      id: child.id,
      label: child.title,
      link: child.url_path,
      groupIndex,
      groupLabel: undefined,
    });

    if (child.children?.length) {
      flattenChildrenToSubMenus(child.children, subMenus, groupIndex);
    }
  }
  return subMenus;
};

const transformMenuToHeaderData = (
  menu: IMenuItem[],
  isMegaMenu = false
): HeaderMenuElementDataType[] => {
  return menu.map((item) => {
    const hasChildren = item.children && item.children.length > 0;

    return {
      name: item.title,
      label: item.title,
      link: item.url_path || undefined,
      subMenuType: !hasChildren ? 'NONE' : isMegaMenu ? 'MEGA_MENU' : 'DROPDOWN',
      dropdownWidth: 'AUTO',
      megaMenuWidth: 'FULL_WIDTH',
      subMenus: hasChildren ? flattenChildrenToSubMenus(item.children) : [],
    };
  });
};
